import { toast } from "sonner";

import type {
  JackpotWonNotificationSchema,
  JackpotProgressSchema,
} from "@/types";
import type { CampaignKey } from "@/campaigns/campaign-key";
import { JackpotToast } from "@/components/notification/jackpot-toast";
import { useAppStore } from "@/stores";
import { postPrizePayout } from "./host-events";
import { ownsInGameNotifications } from "./notifications";

export function isJackpotWonNotification(
  notification: { type?: string } & object
): notification is JackpotWonNotificationSchema {
  return notification.type === "jackpot_won";
}

/**
 * Toast the reader's own jackpot wins. Deduped against `seenNotificationIds` (a lost ack response
 * re-delivers the row on the next resubscribe). Gated like the mission toasts
 * (`ownsInGameNotifications`): two toasts for one win is worse than one — and, as there, a
 * pending win is toasted whatever its age, since only this surface acks it. Rows are
 * deliberately NOT marked seen while gated, so flipping the casino to provider mid-session lets a
 * re-delivery toast normally.
 */
export function showJackpotNotifications(
  notifications: JackpotWonNotificationSchema[]
): void {
  const state = useAppStore.getState();
  if (!ownsInGameNotifications(state)) return;
  const unseen = notifications.filter(
    (n) => !(n.id in state.seenNotificationIds)
  );
  if (unseen.length === 0) return;
  state.markNotificationsSeen(unseen.map((n) => n.id));

  const toasted: number[] = [];
  for (const notification of unseen) {
    toasted.push(notification.campaign_id);
    toast.custom((id) => (
      <JackpotToast
        campaignId={notification.campaign_id}
        copy={{
          kind: "won",
          amount: notification.payout_amount,
          jackpotTitle: notification.jackpot_title,
        }}
        onDismiss={() => toast.dismiss(id)}
      />
    ));
    postPrizePayout(notification.id, {
      text: `${notification.payout_amount} · ${notification.jackpot_title}`,
    });
  }
  state.noteCampaignNews(toasted);
}

/** A win older than this on a progress push is history catching up, not news. The stream drains
 *  a win within its 5 s dispatch interval, so a real one is always well inside the window. */
const ANNOUNCE_FRESHNESS_MS = 60_000;

/** Rows already announced this session, so a re-push of the same feed (every update carries the
 *  full `recent_wins`) never repeats a toast. Bounded: the feed itself is capped server-side. */
const announced = new Set<string>();
const ANNOUNCED_CAP = 200;

const winKey = (win: { jackpot_id: number; won_at: string }) =>
  `${win.jackpot_id}-${win.won_at}`;

/**
 * Toast other players' wins for everyone watching. There is no per-viewer notification for
 * this — the shared `jackpot_progress` stream is the one channel every viewer has — so a win is
 * detected as a `recent_wins` row that was not in the previous push for a campaign we already
 * knew. The reader's own win is skipped here: it arrives as their `jackpot_won` notification and
 * gets the gold card instead. Called by the progress handler on live updates only, never on the
 * subscribe page (that would replay the feed's history as a burst of toasts).
 */
export function announceJackpotWins(
  previous: Record<CampaignKey, JackpotProgressSchema>,
  rows: JackpotProgressSchema[]
): void {
  const state = useAppStore.getState();
  // Same gate as the reader's own win above. Someone else's win is no news on the player's own
  // campaign list, so it lights no rail dot.
  if (!ownsInGameNotifications(state)) return;
  const ownHash =
    state.playerHash ?? (state.authData?.player_hash as string) ?? null;
  const cutoff = Date.now() - ANNOUNCE_FRESHNESS_MS;

  const previousKeys = new Map<number, Set<string>>();
  for (const entry of Object.values(previous)) {
    previousKeys.set(entry.id, new Set((entry.recent_wins ?? []).map(winKey)));
  }

  for (const entry of rows) {
    const before = previousKeys.get(entry.id);
    // First sight of this campaign on the stream: its feed is history, not news.
    if (!before) continue;
    const campaign = Object.values(state.campaignsById).find(
      (c) => c.id === entry.id
    );
    const titleById = new Map(
      (campaign?.jackpots ?? []).map((j) => [j.id, j.title])
    );
    for (const win of entry.recent_wins ?? []) {
      const key = winKey(win);
      if (before.has(key) || announced.has(key)) continue;
      remember(key);
      if (win.player === ownHash) continue;
      if (Date.parse(win.won_at) < cutoff) continue;
      toast.custom((id) => (
        <JackpotToast
          campaignId={entry.id}
          copy={{
            kind: "announce",
            name: win.name,
            amount: win.amount.display,
            jackpotTitle: titleById.get(win.jackpot_id) ?? "",
          }}
          onDismiss={() => toast.dismiss(id)}
        />
      ));
    }
  }
}

function remember(key: string): void {
  if (announced.size >= ANNOUNCED_CAP) {
    const oldest = announced.values().next().value;
    if (oldest !== undefined) announced.delete(oldest);
  }
  announced.add(key);
}
